import React, {useEffect, useState} from "react";
import {Button, Card, Checkbox, Col, Form, Input, InputNumber, Row, Select, Space} from "antd";
import {AgentNewContext} from "../../agent.new.modal";
import {useTranslate} from "../../../../../../hooks/translate.hook";
import {useRequiredStore} from "../../../../../../utils/store";
import {RootStoreContext} from "../../../../../../stores/root/root.store";
import {paymentTermsDays} from "../../../agent-data-general/agent-data-price/agent.data.price";
import {observer} from "mobx-react";
import {Explanation} from "../../../../../common/explanation/explanation";
import {useAgentNewStepCardStyles} from "../agent.new.step.card.styles";
import {
    UIInputWidgetAccountShipmentProcedure,
} from "../../../../ui/input/account-shipment-procedure/ui.input.account-shipment-procedure";

export const PriceAndShipmentsStep: React.FC = observer(() => {

    const t = useTranslate();
    const cardStyles = useAgentNewStepCardStyles();
    const { commonStore } = useRequiredStore(RootStoreContext);

    const [loading, setLoading] = useState<boolean>(false);

    const [form] = Form.useForm();

    const {setStep, getValue, setValue, fields} = React.useContext(AgentNewContext);

    useEffect(() => {
        form.setFields(fields);
    }, [fields])

    const defaultValues = {
        price: getValue('price'),
        shipments: getValue('shipments'),
    }

    return <Space direction="vertical" size={[15, 15]} style={{width: "100%"}}>
        <Form
            size={"middle"}
            form={form}
            layout="vertical"
            name={"client_form"}
            initialValues={defaultValues}
            onFinish={async (values) => {
                await form.validateFields();
                setValue('price', values.price)
                setValue('shipments', values.shipments)
                setStep(3)
            }}
        >
            <Row gutter={[16, 16]} align="top">
                <Col xs={24} md={12}>
                    <Card
                        size="small"
                        title={t("CLIENT.PRICE.SETTINGS", {defaultValue: "Price settings"})}
                        style={{width: "100%"}}
                        styles={cardStyles}
                    >
                        <Form.Item
                            name={["price", "price"]}
                            label={t("PRICE_LIST")}
                            rules={[
                                {
                                    required: true,
                                    message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                },
                            ]}
                        >
                            <Select
                                placeholder={t("PRICE_LIST")}
                                showSearch
                                disabled={loading}
                                optionFilterProp="children"
                            >
                                {commonStore.priceLists.map((item) => (
                                    <Select.Option key={item.id} value={item.id}>
                                        {item.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Row gutter={16}>
                            <Col xs={24} sm={24}>
                                <Form.Item
                                    name={["price", "discount"]}
                                    label={(
                                        <Explanation
                                            title={t("DISCOUNT")}
                                            tooltip={t("DISCOUNT_EXPL")}
                                        />
                                    )}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                        },
                                    ]}
                                >
                                    <InputNumber
                                        disabled={loading}
                                        placeholder={`${t("EXAMPLE")}: -10`}
                                        style={{width: "100%"}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24}>
                                <Form.Item
                                    name={["price", "paymentTerms"]}
                                    label={t("PAYMENT_TERMS")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                        },
                                    ]}
                                >
                                    <Select disabled={loading}>
                                        {paymentTermsDays.map((value) => (
                                            <Select.Option key={value} value={value}>
                                                {value} {t("DAYS")}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name={["price", "applyMinimalPrice"]} valuePropName="checked">
                            <Checkbox>
                                <Explanation
                                    title={t("CLIENT.APPLY_MINIMAL_PRICE")}
                                    tooltip={t("CLIENT.APPLY_MINIMAL_PRICE_EXPL")}
                                />
                            </Checkbox>
                        </Form.Item>
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card
                        size="small"
                        title={t("CLIENT.SHIPMENTS.SETTINGS", {defaultValue: "Shipment settings"})}
                        style={{width: "100%"}}
                        styles={cardStyles}
                    >
                        <Row gutter={[16, 0]} align="top">
                            <Col span={24}>
                                <Form.Item
                                    name={["shipments", "shipmentCondition"]}
                                    label={(
                                        <Explanation
                                            title={t("SHIPMENT_CONDITION")}
                                            tooltip={(
                                                <>
                                                    {t("SHIPMENT_CONDITION_EXPL")}<br />
                                                    <a
                                                        href="https://en.wikipedia.org/wiki/Incoterms"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        https://en.wikipedia.org/wiki/Incoterms
                                                    </a>
                                                </>
                                            )}
                                        />
                                    )}
                                >
                                    <Input
                                        disabled={loading}
                                        type="text"
                                        placeholder={t("SHIPMENT_CONDITION")}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name={["shipments", "shipmentProcedureName"]}
                                    label={(
                                        <Explanation
                                            title={t("SHIPMENT_PROCEDURE")}
                                            tooltip={t("SHIPMENT_PROCEDURE_EXPL")}
                                        />
                                    )}
                                >
                                    <UIInputWidgetAccountShipmentProcedure disabled={loading} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name={["shipments", "customShipper"]}
                                    label={t("CLIENT.CUSTOM_SHIPPER")}
                                >
                                    <Select
                                        allowClear
                                        disabled={loading}
                                        loading={loading}
                                        placeholder={t("CLIENT.CUSTOM_SHIPPER")}
                                    >
                                        {commonStore.shippers.map(({id, companyName, personName}) => (
                                            <Select.Option key={id} value={id}>
                                                {companyName} ({personName})
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
            <Row gutter={[30, 0]} align="top" style={{marginTop: 16}}>
                <Col span={24}>
                    <Space direction="horizontal" size={[10, 10]}>
                        <Button type="default" onClick={() => setStep(1)}>Back</Button>
                        <Button type="primary" onClick={() => form.submit()}>Next</Button>
                    </Space>
                </Col>
            </Row>
        </Form>
    </Space>
})
